// Greenhold component prototype: <RevealCeremony> (Epic 8 design pass).
//
// Mounted at /prototype/reveal-ceremony (no guard) — the team's MVP-
// Storybook substitute. Exercises the ported ceremony: approach → breaking
// → revealing → settled, across the four tiers, with the reduced-motion
// resolution shown live.
//
// This prototype BYPASSES the useReveal mutation. Clicking a tier mounts
// the ceremony in `approach` with the sealed dossier; "Break the seal"
// simulates the server reveal with a short latency (setTimeout) so the
// deferred-share path (real first-open) is exercised — the certificate
// resolves mid-break and rises out of the opening dossier.

import { useState } from "react";
import {
  Button,
  PatronageCertificate,
  RevealCeremony,
  isLoadableImageUrl,
  type CertificateShare,
  type PatronageCertificateProps,
  type SealedDossierData,
} from "@greenhold/components";

import { certSerialFromShareId } from "../lib/certSerial";
import { useEffectiveMotionMode } from "../features/preferences/useEffectiveMotionMode";
import { useEffectiveMobileFidelity } from "../features/preferences/useMobileFidelity";
import { useCeremonyFpsGuard } from "../features/preferences/useCeremonyFpsGuard";

function makeShare(
  tier: CertificateShare["tier"],
  over: Partial<CertificateShare>,
): CertificateShare {
  return {
    share_id: "11111111-1111-1111-1111-111111111111",
    dossier_id: "aaaaaaaa-1111-1111-1111-111111111111",
    drop_id: "dddddddd-1111-1111-1111-111111111111",
    portfolio_id: "pppppppp-1111-1111-1111-111111111111",
    portfolio_title: "Worlds 26 Showdown",
    tier,
    edition_number: 42,
    share_serial: 401,
    creator_id: "cccccccc-1111-1111-1111-111111111111",
    creator_handle: "creator",
    snapshot_display_name: "Creator",
    snapshot_hello_text: "You just collected one of my moments — thank you.",
    snapshot_image_url: "creators/portrait.jpg",
    snapshot_bio: "Esports creator.",
    snapshot_image_signed_url: "https://picsum.photos/seed/gh/256/256",
    snapshot_image_signed_url_expires_at: "2026-05-24T18:00:00Z",
    issued_at: "2026-05-24T12:34:56Z",
    opened_at: "2026-05-24T12:35:01Z",
    is_highlighted_by_owner: false,
    is_favourited_by_owner: false,
    condition: 0.04,
    signed: tier === "founders",
    ...over,
  };
}

const FIXTURES: ReadonlyArray<{ label: string; share: CertificateShare }> = [
  { label: "Common", share: makeShare("common", { snapshot_display_name: "Marie", creator_handle: "marie" }) },
  { label: "Preferred", share: makeShare("preferred", { snapshot_display_name: "Sjokz", creator_handle: "sjokz" }) },
  { label: "Premium", share: makeShare("premium", { snapshot_display_name: "Caps", creator_handle: "caps" }) },
  { label: "Founder's", share: makeShare("founders", { snapshot_display_name: "Faker", creator_handle: "faker", edition_number: 1 }) },
];

const mockDossier: SealedDossierData = {
  id: "aaaaaaaa-1111-1111-1111-111111111111",
  drop_id: "dddddddd-1111-1111-1111-111111111111",
  edition_number: 42,
  portfolio_title: "Worlds 26 Showdown",
  drop_status: "live",
  retiring_at: null,
};

function certProps(share: CertificateShare): PatronageCertificateProps {
  const portraitUrl = isLoadableImageUrl(share.snapshot_image_signed_url)
    ? share.snapshot_image_signed_url
    : null;
  return {
    tier: share.tier,
    name: share.snapshot_display_name,
    serial: certSerialFromShareId(share.share_id),
    dossier: share.portfolio_title,
    issued: share.opened_at.slice(0, 10),
    condition: share.condition,
    signed: share.signed,
    portraitUrl,
    quote: share.snapshot_hello_text || undefined,
  };
}

export function RevealCeremonyPrototype() {
  // The fixture chosen to open; and the "revealed" share (null until the
  // simulated reveal resolves after "Break the seal").
  const [target, setTarget] = useState<CertificateShare | null>(null);
  const [revealed, setRevealed] = useState<CertificateShare | null>(null);
  const effectiveMode = useEffectiveMotionMode();
  // Story 6.6 — reduced mobile-fidelity when the viewport is phone-sized
  // OR the runtime FPS guard has auto-degraded this session.
  const mobileFidelity = useEffectiveMobileFidelity();
  // Story 6.6 — sample real-user FPS for as long as a ceremony is mounted;
  // if it drops frames the guard latches the session into mobileFidelity.
  useCeremonyFpsGuard(target !== null);

  return (
    <main className="min-h-screen bg-brand-paper-100 text-brand-forest-900 p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        <header>
          <h1 className="text-3xl font-heading font-bold mb-2">
            &lt;RevealCeremony&gt; prototype
          </h1>
          <p className="opacity-80 max-w-3xl leading-relaxed">
            Break a sealed dossier open to reveal the certificate inside.
            approach → breaking (3D gatefold split + burst light) → revealing
            (certificate rises) → settled. Tier-scaled timing (common 4.5s →
            founder's 10s); reduced-motion collapses to ~0.6s.
          </p>
          <p className="text-xs font-mono opacity-70 mt-3">
            Resolved motion mode (useEffectiveMotionMode):{" "}
            <span className="font-bold">{effectiveMode}</span>
          </p>
          <p className="text-xs font-mono opacity-70 mt-1">
            Effective mobile-fidelity (Story 6.6 auto-degrade):{" "}
            <span className="font-bold" data-testid="effective-mobile-fidelity">
              {mobileFidelity ? "reduced" : "full"}
            </span>
          </p>
        </header>

        <section>
          <h2 className="text-sm uppercase tracking-wider font-mono opacity-70 mb-3">
            Open a dossier
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {FIXTURES.map((f) => (
              <Button
                key={f.label}
                variant="secondary"
                onClick={() => {
                  setRevealed(null);
                  setTarget(f.share);
                }}
              >
                {f.label}
              </Button>
            ))}
          </div>
          <p className="text-sm opacity-70 mt-3 leading-relaxed">
            Bypasses <code>useReveal</code>. "Break the seal" simulates the
            server reveal with a 400ms latency, so the certificate resolves
            mid-break (the real first-open path).
          </p>
        </section>

        {target && (
          <RevealCeremony
            open
            dossier={mockDossier}
            share={revealed}
            certificate={
              revealed ? <PatronageCertificate {...certProps(revealed)} /> : null
            }
            serial={revealed ? certSerialFromShareId(revealed.share_id) : undefined}
            theme="Counter-Strike"
            creators={[
              { name: "Marie", img: "https://picsum.photos/seed/gh1/96/96" },
              { name: "Sjokz", img: "https://picsum.photos/seed/gh2/96/96" },
              { name: "Caps", img: "https://picsum.photos/seed/gh3/96/96" },
              { name: "Faker", img: "https://picsum.photos/seed/gh4/96/96" },
              { name: "Rekkles", img: null },
            ]}
            reducedMotion={effectiveMode === "reduced-motion"}
            mobileFidelity={mobileFidelity}
            onBreak={() => {
              // Simulate the reveal mutation resolving after a short delay.
              window.setTimeout(() => setRevealed(target), 400);
            }}
            onViewCertificate={() => {
              setTarget(null);
              setRevealed(null);
            }}
            onClose={() => {
              setTarget(null);
              setRevealed(null);
            }}
          />
        )}
      </div>
    </main>
  );
}
